import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { VehicleDetailModification } from './vehicle-detail/types';

export interface MoveModificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Parts being moved (one or many). */
  targets: VehicleDetailModification[];
  allModifications: VehicleDetailModification[];
  mode?: 'single' | 'bulk';
  onConfirm: (result: {
    parent_modification_id: number | null;
    category?: string;
    before_modification_id?: number | null;
    after_modification_id?: number | null;
  }) => Promise<void> | void;
}

type Placement =
  | { kind: 'end' }
  | { kind: 'start' }
  | { kind: 'after'; id: string }
  | { kind: 'before'; id: string };

function pathFor(
  mod: VehicleDetailModification,
  byId: Map<string, VehicleDetailModification>
): string {
  const parts: string[] = [];
  let currentId: string | null = String(mod.id);
  const guard = new Set<string>();
  while (currentId) {
    if (guard.has(currentId)) break;
    guard.add(currentId);
    const current = byId.get(currentId);
    if (!current) break;
    parts.unshift(current.title);
    currentId =
      current.parent_modification_id != null
        ? String(current.parent_modification_id)
        : null;
  }
  const cat = mod.category || 'Miscellaneous';
  return `${cat} › ${parts.join(' › ')}`;
}

function isDescendantOf(
  candidateId: string,
  ancestorId: string,
  byId: Map<string, VehicleDetailModification>
): boolean {
  let cur = byId.get(candidateId);
  const guard = new Set<string>();
  while (cur?.parent_modification_id != null) {
    const pid = String(cur.parent_modification_id);
    if (pid === ancestorId) return true;
    if (guard.has(pid)) return false;
    guard.add(pid);
    cur = byId.get(pid);
  }
  return false;
}

function siblingsUnderParent(
  all: VehicleDetailModification[],
  parentId: string | null,
  excludeIds: Set<string>
): VehicleDetailModification[] {
  return all
    .filter((m) => {
      const pid = m.parent_modification_id != null ? String(m.parent_modification_id) : null;
      return pid === parentId && !excludeIds.has(String(m.id));
    })
    .sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.title.localeCompare(b.title)
    );
}

export const MoveModificationModal: React.FC<MoveModificationModalProps> = ({
  isOpen,
  onClose,
  targets,
  allModifications,
  mode = 'single',
  onConfirm,
}) => {
  const [query, setQuery] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [topLevelCategory, setTopLevelCategory] = useState<string>('');
  const [placement, setPlacement] = useState<Placement>({ kind: 'end' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const byId = useMemo(() => {
    const map = new Map<string, VehicleDetailModification>();
    allModifications.forEach((m) => map.set(String(m.id), m));
    return map;
  }, [allModifications]);

  const targetIds = useMemo(() => new Set(targets.map((t) => String(t.id))), [targets]);

  const primary = targets[0];

  useEffect(() => {
    if (!isOpen || !primary) return;
    setQuery('');
    setError(null);
    setBusy(false);
    setSelectedParentId(null);
    setPlacement({ kind: 'end' });
    setTopLevelCategory(primary.category || 'Miscellaneous');
    const t = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [isOpen, primary]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, busy, onClose]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    allModifications.forEach((m) => {
      if (m.category) set.add(m.category);
    });
    if (primary?.category) set.add(primary.category);
    if (!set.has('Miscellaneous')) set.add('Miscellaneous');
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allModifications, primary]);

  const blockedParent = (candidate: VehicleDetailModification): boolean => {
    const cid = String(candidate.id);
    if (targetIds.has(cid)) return true;
    for (const t of targets) {
      if (isDescendantOf(cid, String(t.id), byId)) return true;
    }
    return false;
  };

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allModifications
      .filter((m) => !blockedParent(m))
      .filter((m) => {
        if (!q) return true;
        const hay = `${m.title} ${m.vendor || ''} ${m.category || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 40);
    // blockedParent closes over targets/byId; recompute when those change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allModifications, query, targetIds, byId]);

  const destinationSiblings = useMemo(
    () => siblingsUnderParent(allModifications, selectedParentId, targetIds),
    [allModifications, selectedParentId, targetIds]
  );

  if (!isOpen || !primary) return null;

  const currentPath =
    targets.length === 1
      ? pathFor(primary, byId)
      : `${targets.length} selected parts`;

  const resultingPath =
    selectedParentId === null
      ? `Top level in ${topLevelCategory}`
      : pathFor(byId.get(selectedParentId)!, byId) + ' › (inside)';

  const placementLabel = (() => {
    if (placement.kind === 'start') return 'at the start';
    if (placement.kind === 'end') return 'at the end';
    if (placement.kind === 'after') {
      const sib = byId.get(placement.id);
      return sib ? `after “${sib.title}”` : 'after selected part';
    }
    const sib = byId.get(placement.id);
    return sib ? `before “${sib.title}”` : 'before selected part';
  })();

  const chooseParent = (id: string | null) => {
    setSelectedParentId(id);
    setPlacement({ kind: 'end' });
  };

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      let before_modification_id: number | null | undefined;
      let after_modification_id: number | null | undefined;
      if (placement.kind === 'start' && destinationSiblings[0]) {
        before_modification_id = parseInt(String(destinationSiblings[0].id), 10);
      } else if (placement.kind === 'before') {
        before_modification_id = parseInt(placement.id, 10);
      } else if (placement.kind === 'after') {
        after_modification_id = parseInt(placement.id, 10);
      }

      await onConfirm({
        parent_modification_id: selectedParentId ? parseInt(selectedParentId, 10) : null,
        category: selectedParentId === null ? topLevelCategory : undefined,
        before_modification_id,
        after_modification_id,
      });
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not move parts');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-overlay z-[70]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-mod-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="modal-sheet sm:max-w-lg overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-4 sm:px-5 py-4 border-b border-white/[0.06]">
          <h2 id="move-mod-title" className="text-lg font-semibold text-white">
            {mode === 'bulk' ? `Move ${targets.length} parts` : `Move “${primary.title}”`}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Current: <span className="text-gray-300">{currentPath}</span>
          </p>
        </div>

        <div className="px-4 sm:px-5 py-3 space-y-3 overflow-auto flex-1">
          <div>
            <label htmlFor="move-search" className="block text-xs text-gray-500 mb-1">
              Move into
            </label>
            <input
              ref={searchRef}
              id="move-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search assemblies or parts"
              className="input text-sm"
            />
          </div>

          <button
            type="button"
            onClick={() => chooseParent(null)}
            className={`w-full text-left rounded-lg border px-3 py-3 min-h-[48px] ${
              selectedParentId === null
                ? 'border-orange-500/50 bg-orange-500/10 text-orange-200'
                : 'border-white/[0.06] text-gray-300 hover:bg-white/[0.04]/40'
            }`}
          >
            <span className="text-sm font-medium">Top level</span>
            <span className="block text-[11px] text-gray-500 mt-0.5">
              Not nested under another part
            </span>
          </button>

          {selectedParentId === null && (
            <div>
              <label htmlFor="move-cat" className="block text-xs text-gray-500 mb-1">
                Category for top level
              </label>
              <select
                id="move-cat"
                value={topLevelCategory}
                onChange={(e) => setTopLevelCategory(e.target.value)}
                className="input text-sm"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          <ul className="space-y-1" role="listbox" aria-label="Assemblies and parts">
            {suggestions.map((m) => {
              const selected = selectedParentId === String(m.id);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => chooseParent(String(m.id))}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 min-h-[44px] ${
                      selected
                        ? 'border-orange-500/50 bg-orange-500/10 text-orange-200'
                        : 'border-white/[0.06] text-gray-300 hover:bg-white/[0.04]/40'
                    }`}
                  >
                    <span className="text-sm">
                      {m.vendor ? <span className="text-gray-500">{m.vendor} </span> : null}
                      {m.title}
                    </span>
                    <span className="block text-[11px] text-gray-500 mt-0.5 truncate">
                      {pathFor(m, byId)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {destinationSiblings.length > 0 && (
            <div className="rounded-lg border border-white/[0.06] bg-[#0a0a0a]/50 p-3 space-y-2">
              <p className="text-xs font-medium text-gray-300">Place among siblings</p>
              {mode === 'bulk' && (
                <p className="text-[11px] text-gray-500">
                  Bulk moves keep relative order and insert as a block.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setPlacement({ kind: 'start' })}
                  className={`text-xs min-h-[36px] px-3 rounded-lg border ${
                    placement.kind === 'start'
                      ? 'border-orange-500/50 text-orange-200 bg-orange-500/10'
                      : 'border-white/[0.08] text-gray-300'
                  }`}
                >
                  At start
                </button>
                <button
                  type="button"
                  onClick={() => setPlacement({ kind: 'end' })}
                  className={`text-xs min-h-[36px] px-3 rounded-lg border ${
                    placement.kind === 'end'
                      ? 'border-orange-500/50 text-orange-200 bg-orange-500/10'
                      : 'border-white/[0.08] text-gray-300'
                  }`}
                >
                  At end
                </button>
              </div>
              <ul className="max-h-40 overflow-y-auto space-y-1" aria-label="Sibling placement">
                {destinationSiblings.map((sib) => {
                  const afterSelected =
                    placement.kind === 'after' && placement.id === String(sib.id);
                  return (
                    <li key={sib.id}>
                      <button
                        type="button"
                        onClick={() => setPlacement({ kind: 'after', id: String(sib.id) })}
                        className={`w-full text-left text-xs min-h-[40px] px-3 rounded-lg border ${
                          afterSelected
                            ? 'border-orange-500/50 text-orange-200 bg-orange-500/10'
                            : 'border-white/[0.06] text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        After “{sib.title}”
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <p className="text-xs text-gray-400">
            Resulting location:{' '}
            <span className="text-gray-200">
              {resultingPath} · {placementLabel}
            </span>
          </p>
          {error && (
            <p className="text-sm text-red-400" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-2 px-4 sm:px-5 py-4 border-t border-white/[0.06]">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleConfirm}
            className="btn-primary ml-auto disabled:opacity-50"
          >
            {busy ? 'Moving…' : 'Move here'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveModificationModal;
